import { ChangeDetectorRef, Component, Input, OnInit, ViewChild, OnChanges, SimpleChanges } from '@angular/core';
import { StylometricStateService } from '../../../services/stylometric-state.service';
import { ANALYSIS_IDS } from '../../../models/stylometric.model';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexXAxis,
  ApexDataLabels,
  ApexPlotOptions,
  ApexYAxis,
  ApexLegend,
  ApexStroke,
  ApexFill,
  ApexMarkers,
  ChartComponent,
  ApexGrid
} from "ng-apexcharts";
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { AiAnalysisComponent } from '../../sidebar-components/metadata/ai_analysis/ai_analysis.component';

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  dataLabels: ApexDataLabels;
  plotOptions: ApexPlotOptions;
  yaxis: ApexYAxis;
  legend: ApexLegend;
  fill: ApexFill;
  stroke: ApexStroke;
  markers: ApexMarkers;
  colors: string[];
  grid: ApexGrid;
};

@Component({
  selector: 'app-stylometric-analysis',
  templateUrl: './stylometric-analysis.component.html',
  styleUrls: ['./stylometric-analysis.component.scss']
})
export class StylometricAnalysisComponent implements OnInit, OnChanges {
  @Input() submission: any;
  @Input() submissionId: string;
  @Input() documentId: string;
  @Input() stylometricMetrics: any;
  @Input() previousSubmissions: any;
  @Input() author: string;

  selectedTabId: (typeof ANALYSIS_IDS)[keyof typeof ANALYSIS_IDS] = ANALYSIS_IDS.LEXICAL;
  previousMetrics: any = null;
  selectedSubmissionId: string = 'all';
  @Input() metricsThresholds: any;
  tabs = [
    { id: ANALYSIS_IDS.LEXICAL, name: 'Lexical Analysis', count: 0 },
    { id: ANALYSIS_IDS.SYNTACTIC, name: 'Syntactic Analysis', count: 0 },
    { id: ANALYSIS_IDS.STRUCTURAL, name: 'Structural Analysis', count: 0 },
    { id: ANALYSIS_IDS.READABILITY, name: 'Readability Analysis', count: 0 }
  ];

  metrics = {
    lexical: [
      {
        name: 'Hapax Ratio',
        description: 'Measures the proportion of words that appear once, relative to the total word count.',
        avgPrevious: '0.12',
        current: '0.26'
      },
      {
        name: 'Character Diversity',
        description: 'Reflects the range of unique characters in a text. Low diversity indicates frequent character repetition.',
        avgPrevious: '0.007',
        current: '0.009'
      },
      {
        name: 'Average Word Length',
        description: 'Refers to the mean length of words in a text. Higher values suggest more complex language.',
        avgPrevious: '4.8',
        current: '5.5'
      },
      {
        name: 'Type-Token Ratio',
        description: 'Measures lexical diversity by dividing the number of unique words by the total number of words in a text.',
        avgPrevious: '0.35',
        current: '0.39'
      },
      {
        name: 'Punctuation Ratio',
        description: 'Measures punctuation frequency relative to word count. Higher values suggest more structured writing.',
        avgPrevious: '0.13',
        current: '0.15'
      },
      {
        name: 'Unique Words Ratio',
        description: 'Measures the proportion of unique words relative to the total word count, indicating the text\'s lexical diversity.',
        avgPrevious: '0.35',
        current: '0.39'
      },
      {
        name: 'Vocabulary Richness',
        description: 'Measures unique word proportion, with higher values indicating greater lexical diversity.',
        avgPrevious: '0.35',
        current: '0.39'
      },
      {
        name: 'Word Length Variance',
        description: 'Measures the variation in word lengths throughout the text.',
        avgPrevious: '8.1',
        current: '9.1'
      }
    ],
    syntactic: [
      {
        name: 'Parse Tree Breadth',
        description: 'Measures sentence structure width with higher values indicating more complex syntactic relationships.',
        avgPrevious: '23.2',
        current: '25.3'
      },
      {
        name: 'Average Sentence Length',
        description: 'Refers to the mean number of words per sentence. Higher values suggest more complex sentences.',
        avgPrevious: '22.1',
        current: '24.3'
      },
      {
        name: 'Function Word Ratio',
        description: 'Measures the proportion of function words by dividing the number of function words by the total number of words in a text. Higher values indicate formal or academic writing.',
        avgPrevious: '0.29',
        current: '0.32'
      },
      {
        name: 'Sentence Complexity',
        description: 'Quantifies the difficulty of understanding text by analyzing its grammatical and lexical structure.',
        avgPrevious: '1.9',
        current: '2.1'
      },
      {
        name: 'Syntactic Diversity',
        description: 'Measures sentence structure variety, with higher values indicating more diverse and complex sentence structures.',
        avgPrevious: '0.07',
        current: '0.08'
      },
      {
        name: 'Average Parse Tree Depth',
        description: 'Refers to the average number of levels in a sentence\'s structure. Higher values indicate more complex syntactic arrangements.',
        avgPrevious: '6.5',
        current: '7.1'
      },
      {
        name: 'Subordinate Clause Ratio',
        description: 'The Subordinate Clause Ratio is the number of subordinate clauses divided by total number of clauses, with higher values indicating more complexity.',
        avgPrevious: '0.029',
        current: '0.032'
      }
    ],
    structural: [
      {
        name: 'Text Density',
        description: 'Measures meaningful content per word with higher values indicating more complex, information-rich text and lower values suggesting simplicity.',
        avgPrevious: '0.82',
        current: '0.84'
      },
      {
        name: 'Paragraph Count',
        description: 'Measures the number of paragraphs in a text, with higher values indicating more detailed or segmented content.',
        avgPrevious: '2',
        current: '1'
      },
      {
        name: 'Whitespace Ratio',
        description: 'Reflects the proportion of whitespace to text, with higher values indicating shorter paragraphs or frequent line breaks and lower values indicating denser text.',
        avgPrevious: '0.18',
        current: '0.16'
      },
      {
        name: 'Line Break Frequency',
        description: 'Line Break Frequency measures breaks in text, with higher values indicating shorter sections and lower values indicating longer passages.',
        avgPrevious: '0.95',
        current: '0.99'
      },
      {
        name: 'Structure Consistency',
        description: 'Structure Consistency measures uniformity in text organization, considering sentences, paragraphs and sections, with higher values indicating consistency.',
        avgPrevious: '0.1',
        current: '0'
      },
      {
        name: 'Paragraph Length Variance',
        description: 'Paragraph Length Variance measures the variation in paragraph lengths within a text, indicating how much paragraph lengths differ from the average. Higher values suggest more complex paragraph structures.',
        avgPrevious: '0.1',
        current: '0'
      },
      {
        name: 'Average Sentences per Paragraph',
        description: 'Average Sentences per Paragraph measures the mean number of sentences in each paragraph, with higher values suggesting longer, more detailed paragraphs.',
        avgPrevious: '45',
        current: '58'
      }
    ],
    readability: [
      {
        name: 'SMOG Index',
        description: 'The SMOG Index measures readability based on polysyllabic words with higher values indicating more complex text that requires a higher education level to understand.',
        avgPrevious: '14.5',
        current: '15.2'
      },
      {
        name: 'Gunning Fog',
        description: 'The Gunning Fog Index estimates readability based on sentence length and complex words with higher values indicating more difficulty.',
        avgPrevious: '12.8',
        current: '13.47'
      },
      {
        name: 'Complex Word Ratio',
        description: 'The Complex Word Ratio is calculated by dividing the number of complex words by the total words, with higher values indicating greater complexity.',
        avgPrevious: '0.19',
        current: '0.22'
      },
      {
        name: 'Flesch Reading Ease',
        description: 'The Flesch Reading Ease measures readability based on the average sentence length and average syllables per word, with higher values indicating easier readability.',
        avgPrevious: '38.2',
        current: '34.36'
      },
      {
        name: 'Flesch Kincaid Grade',
        description: 'The Flesch-Kincaid Grade Level measures readability based on syllables per word and words per sentence, with lower values indicating easier readability.',
        avgPrevious: '12.8',
        current: '13.4'
      },
      {
        name: 'Average Syllables per Word',
        description: 'The Average Syllables per Word is calculated by dividing total syllables by the total number of words, with higher values indicating more complex language.',
        avgPrevious: '1.7',
        current: '1.8'
      },
      {
        name: 'Automated Readability Index',
        description: 'The Automated Readability Index calculates readability based on sentence and word length, with higher values indicating more complexity.',
        avgPrevious: '15.2',
        current: '16.4'
      }
    ]
  };

  metricsStats = {
    total: 8,
    flagged: 4,
    unflagged: 4
  };

  currentAnalysis = 'lexical';


  isOnIA = false;

  @Input() projectSynapse: boolean = false;
  @Input() projectSynapseData: any;
  currentProjectSynapseData: any;
  currentModelComparisonData: any = null;
  isProjectSynapseActive = false;

  private readonly LARGE_CHART_SIZE = 100;
  private readonly MEDIUM_CHART_SIZE = 80;
  private readonly SMALL_CHART_SIZE = 60;
  private readonly SMALLER_CHART_SIZE = 50;
  private readonly SMALLER_CHART_SIZE_2 = 40;
  private readonly SMALLER_CHART_SIZE_3 = 30;

  @ViewChild("chart") chart: ChartComponent;
  public chartOptions: Partial<ChartOptions>;

  constructor(
    private stylometricState: StylometricStateService,
    private translate: TranslateService,
    private dialog: MatDialog,
    private spinnerService: NgxSpinnerService,
    private cdr: ChangeDetectorRef
  ) {
    // Remove chart initialization from constructor
  }

  private initializeChartOptions(): void {
    const initialSize = window.innerWidth <= 350
      ? this.SMALLER_CHART_SIZE_2
      : window.innerWidth <= 450
        ? this.SMALLER_CHART_SIZE
        : window.innerWidth <= 550
          ? this.MEDIUM_CHART_SIZE
          : window.innerWidth <= 768
            ? this.LARGE_CHART_SIZE
            : window.innerWidth <= 900
              ? this.LARGE_CHART_SIZE
              : window.innerWidth <= 1024
                ? this.SMALLER_CHART_SIZE_2
                : window.innerWidth <= 1224
                  ? this.SMALLER_CHART_SIZE
                  : window.innerWidth <= 1440
                    ? this.LARGE_CHART_SIZE
                    : window.innerWidth <= 1500
                      ? this.SMALL_CHART_SIZE
                      : window.innerWidth <= 1800
                        ? this.MEDIUM_CHART_SIZE
                        : this.LARGE_CHART_SIZE;

    const currentMetrics = this.stylometricMetrics?.[this.currentAnalysis] || [];

    // Get previous metrics based on selection
    const previousMetrics = this.getPreviousMetricsData();
    const graphHeaderHeight = document.querySelector('.graph-header')?.clientHeight;


    this.chartOptions = {
      series: [
        {
          name: "Current submission",
          data: currentMetrics.map(metric => Number(metric.value))
        },
        {
          name: "AVG. Previous submissions",
          data: previousMetrics.map(metric => Number(metric.value))
        }
      ],
      chart: {
        type: "radar",
        // height: 'calc(100% - 20px)',
        toolbar: { show: false },
        fontFamily: 'Inter, sans-serif',
        dropShadow: {
          enabled: false
        }
      },
      legend: {
        show: true,
        position: 'bottom',
        // horizontalAlign: 'center',
        offsetY: 15,
        fontSize: '12px',
        fontFamily: 'Inter, sans-serif',
        markers: {
          width: 12,
          height: 12,
          radius: 2
        },
        // itemMargin: {
        //   horizontal: 30,
        //   vertical: 0
        // }
      },
      plotOptions: {
        radar: {
          size: initialSize,
          polygons: {
            strokeColors: '#e8e8e8',
            strokeWidth: '1',
            connectorColors: '#e8e8e8',
            fill: {
              colors: ['#fcfcfc', '#ffffff']
            }
          },
          offsetX: 0,
          offsetY: 0
        }
      },
      grid: {
        show: true,
        xaxis: {
          lines: {
            show: true,
          }
        },
        yaxis: {
          lines: {
            show: true
          }
        }
      },
      colors: ["#D42620", "#147AD6"],
      stroke: {
        width: 2,
        curve: 'smooth'
      },
      fill: {
        opacity: 0.15
      },
      markers: {
        size: 4,
        hover: {
          size: 6
        }
      },
      xaxis: {
        categories: currentMetrics.map(metric => {
          const translationKey = `stylometrics.${this.currentAnalysis}.${metric.name.toLowerCase().replace(/ /g, '_')}`;
          const translatedText = this.translate.instant(translationKey);
          return translatedText.split(' ');
        }),
        labels: {
          rotate: 0,
          style: {
            colors: Array(currentMetrics.length).fill('#666666'),
            fontSize: '10px'
          }
        }
      },
      yaxis: {
        show: false,
        axisBorder: {
          show: false
        },
        axisTicks: {
          show: false
        }
      }
    };
  }

  private getPreviousMetricsData(): any[] {
    if (this.selectedSubmissionId === 'all' && this.previousSubmissions?.all) {
      return this.previousSubmissions.all[this.currentAnalysis] || [];
    } else if (this.previousSubmissions?.individual) {
      const selectedSubmission = this.previousSubmissions.individual.find(
        (sub: any) => sub.filename === this.selectedSubmissionId
      );
      return selectedSubmission?.metrics[this.currentAnalysis] || [];
    }
    return [];
  }

  private updateChartSize(): void {
    const newSize = window.innerWidth <= 350
      ? this.SMALLER_CHART_SIZE_2
      : window.innerWidth <= 450
        ? this.SMALLER_CHART_SIZE
        : window.innerWidth <= 550
          ? this.MEDIUM_CHART_SIZE
          : window.innerWidth <= 768
            ? this.LARGE_CHART_SIZE
            : window.innerWidth <= 900
              ? this.LARGE_CHART_SIZE
              : window.innerWidth <= 1024
                ? this.SMALLER_CHART_SIZE_2
                : window.innerWidth <= 1224
                  ? this.SMALLER_CHART_SIZE
                  : window.innerWidth <= 1440
                    ? this.LARGE_CHART_SIZE
                    : window.innerWidth <= 1500
                      ? this.SMALL_CHART_SIZE
                      : window.innerWidth <= 1800
                        ? this.MEDIUM_CHART_SIZE
                        : this.LARGE_CHART_SIZE;

    if (this.chartOptions?.plotOptions?.radar) {
      this.chartOptions.plotOptions.radar.size = newSize;

      // Update font size for labels
      if (this.chartOptions.xaxis?.labels?.style) {
        this.chartOptions.xaxis.labels.style.fontSize = '10px';
      }

      // Update the chart if it exists
      if (this.chart) {
        this.chart.updateOptions(this.chartOptions);
      }
    }
  }

  ngOnInit() {
    this.isOnIA = !window.location.pathname.split('/').includes('v2');

    // Initialize Project Synapse data early if available
    if (this.projectSynapse && this.projectSynapseData?.length > 0) {
      this.initializeProjectSynapseDataStructure();
    }

    // Listen for Project Synapse state changes
    this.stylometricState.isProjectSynapseActive$.subscribe(
      (isActive: boolean) => {
        // Only update if the state changed
        if (this.isProjectSynapseActive !== isActive) {
          this.isProjectSynapseActive = isActive;

          // Force update of tab counts when Project Synapse state changes
          setTimeout(() => {
            this.updateTabFlagCounts();
            this.cdr.detectChanges();
          }, 100);
        }
      }
    );

    this.stylometricState.selectedAIModel$.subscribe(
      (model: any) => {
        if (model) {
          // Store the full comparison data for the selected model
          this.currentModelComparisonData = model.comparisonData;

          // Force update when Project Synapse data changes
          this.updateTabFlagCounts();
          this.initializeChartOptions();
          this.updateChartData();
          this.cdr.detectChanges();
        } else if (this.projectSynapseData?.length > 0) {
          // Store the comparison data from the first model as fallback
          this.currentModelComparisonData = this.projectSynapseData[0].comparisonData;

          // Force update for initial data
          this.updateTabFlagCounts();
          this.initializeChartOptions();
          this.updateChartData();
          this.cdr.detectChanges();
        }
      }
    );

    // Only update tab counts when metrics data changes
    if (this.stylometricMetrics && this.previousSubmissions) {
      this.updateTabFlagCounts();
    }

    this.stylometricState.selectedAnalysis$.subscribe(
      analysis => {
        if (analysis) {
          this.selectedTabId = analysis as (typeof ANALYSIS_IDS)[keyof typeof ANALYSIS_IDS];
          this.currentAnalysis = analysis.split('-')[0];
          this.initializeChartOptions();
          this.updateChartData();
        }
      }
    );

    this.stylometricState.previousSubmissionMetrics$.subscribe(
      metrics => {
        this.previousMetrics = metrics;
        if (this.chart) {
          this.updateChartData();
        }
        // Update flags when metrics data changes
        this.updateTabFlagCounts();
      }
    );

    this.stylometricState.selectedSubmission$.subscribe(
      submissionId => {
        if (submissionId !== undefined) {
          this.selectedSubmissionId = submissionId;
          // Force chart reinitialization
          this.initializeChartOptions();
          // Update tab flag counts based on selected submission
          this.updateTabFlagCounts();

          if (this.chart) {
            this.updateChartData();
            // Force chart redraw
            this.chart.render();
          }

          // Force change detection to update the UI
          this.cdr.detectChanges();
        }
      }
    );

    // Add resize listener
    window.addEventListener('resize', () => {
      this.updateChartSize();
    });

    // Initial setup
    this.initializeChartOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['stylometricMetrics'] || changes['previousSubmissions'] ||
        changes['metricsThresholds']) {
      // Update data when inputs change
      this.updateTabFlagCounts();
      if (this.chart) {
        this.initializeChartOptions();
        this.updateChartData();
      }

      // Clear cached analysis when metrics or previous submissions change
      this.clearCachedAnalysis();
    }

    // Handle Project Synapse data specifically
    if (changes['projectSynapseData'] && this.projectSynapse) {
      this.initializeProjectSynapseDataStructure();

      // Clear cached analysis when project synapse data changes
      this.clearCachedAnalysis();

      // Force update with a small delay to ensure everything is initialized
      setTimeout(() => {
        this.updateTabFlagCounts();
        this.cdr.detectChanges();
      }, 100);
    }

    // Activate Project Synapse state when the flag changes
    if (changes['projectSynapse'] && changes['projectSynapse'].currentValue !== changes['projectSynapse'].previousValue) {
      this.stylometricState.toggleProjectSynapseActive(changes['projectSynapse'].currentValue);

      // Clear cached analysis when project synapse state changes
      this.clearCachedAnalysis();

      // If Project Synapse is now active and we have data, initialize it
      if (changes['projectSynapse'].currentValue && this.projectSynapseData?.length > 0) {
        this.initializeProjectSynapseDataStructure();

        // Force update with a small delay to ensure everything is initialized
        setTimeout(() => {
          this.updateTabFlagCounts();
          this.cdr.detectChanges();
        }, 100);
      }
    }
  }

  // Method to clear cached analysis
  private clearCachedAnalysis(): void {
    const cacheKey = this.generateCacheKey();
    localStorage.removeItem(cacheKey);
  }

  // New helper method to ensure Project Synapse data is properly initialized
  private initializeProjectSynapseDataStructure(): void {
    if (!this.projectSynapseData || this.projectSynapseData.length === 0) {
      this.currentProjectSynapseData = {};
      this.currentModelComparisonData = null;
      return;
    }

    try {
      const firstModel = this.projectSynapseData[0];

      if (!firstModel || !firstModel.comparisonData) {
        this.currentProjectSynapseData = {};
        this.currentModelComparisonData = null;
        return;
      }

      // Store the full comparison data for the first model
      this.currentModelComparisonData = firstModel.comparisonData;

      // Make sure we have a proper structure even if fields are missing
      if (!firstModel.comparisonData.stylometrics) {
        firstModel.comparisonData.stylometrics = { features: {} };
      } else if (!firstModel.comparisonData.stylometrics.features) {
        firstModel.comparisonData.stylometrics.features = {};
      }

      // Store the legacy data structure for backwards compatibility (if needed)
      this.currentProjectSynapseData = firstModel.comparisonData.stylometrics.features;

      // Update the state service and ensure Project Synapse mode is active
      this.stylometricState.setSelectedAIModel(firstModel);
      this.stylometricState.toggleProjectSynapseActive(true);
      this.isProjectSynapseActive = true;

      // Force flag count updates after data is set with a small delay to ensure UI updates
      setTimeout(() => {
        this.updateTabFlagCounts();
        this.cdr.detectChanges();
      }, 100);
    } catch (error) {
      console.error('Error initializing Project Synapse data:', error);
      this.currentProjectSynapseData = {};
      this.currentModelComparisonData = null;
    }
  }

  ngOnDestroy() {
    // Remove resize listener
    window.removeEventListener('resize', () => {
      this.updateChartSize();
    });
  }

  selectTab(tabId: (typeof ANALYSIS_IDS)[keyof typeof ANALYSIS_IDS]) {
    this.selectedTabId = tabId;
    this.stylometricState.setSelectedAnalysis(tabId);
  }

  get selectedTabName(): string {
    return this.tabs.find(t => t.id === this.selectedTabId)?.name || '';
  }

  private updateChartData(): void {
    if (!this.chart) return;

    const currentMetrics = this.stylometricMetrics[this.currentAnalysis] || [];
    const previousMetrics = this.getPreviousMetricsData();

    // Ensure both arrays have the same order of metrics
    const previousMetricsOrdered = currentMetrics.map(currentMetric => {
      const matchingMetric = previousMetrics.find(pm => pm.name === currentMetric.name);
      return matchingMetric?.value || 0;
    });

    // Calculate chart size based on window width
    const adjustedChartSize = window.innerWidth <= 350
      ? this.SMALLER_CHART_SIZE_2
      : window.innerWidth <= 450
        ? this.SMALLER_CHART_SIZE
        : window.innerWidth <= 550
          ? this.MEDIUM_CHART_SIZE
          : window.innerWidth <= 768
            ? this.LARGE_CHART_SIZE
            : window.innerWidth <= 900
              ? this.LARGE_CHART_SIZE
              : window.innerWidth <= 1024
                ? this.SMALLER_CHART_SIZE_2
                : window.innerWidth <= 1224
                  ? this.SMALLER_CHART_SIZE
                  : window.innerWidth <= 1440
                    ? this.LARGE_CHART_SIZE
                    : window.innerWidth <= 1500
                      ? this.SMALL_CHART_SIZE
                      : window.innerWidth <= 1800
                        ? this.MEDIUM_CHART_SIZE
                        : this.LARGE_CHART_SIZE;

    const newOptions: Partial<ChartOptions> = {
      series: [
        {
          name: "Current submission",
          data: currentMetrics.map(metric => Number(metric.value))
        },
        {
          name: this.selectedSubmissionId === 'all' ?
            "AVG. Previous submissions" :
            "Selected previous submission",
          data: previousMetricsOrdered
        }
      ],
      plotOptions: {
        radar: {
          size: adjustedChartSize,
          offsetX: 0,
          offsetY: 0
        }
      },
      xaxis: {
        categories: currentMetrics.map(metric => {
          const translationKey = `stylometrics.${this.currentAnalysis}.${metric.name.toLowerCase().replace(/ /g, '_')}`;
          const translatedText = this.translate.instant(translationKey);
          return translatedText.split(' ');
        }),
        labels: {
          rotate: 0,
          style: {
            colors: Array(currentMetrics.length).fill('#666666'),
            fontSize: '10px'
          }
        }
      },
      colors: ["#D42620", "#147AD6"],
      stroke: {
        width: 2,
        colors: ["#D42620", "#147AD6"]
      }
    };

    this.chart.updateOptions(newOptions, true, true);
  }

  getPreviousMetricValue(metricName: string): number {
    // Add debug logging
    if (this.isProjectSynapseActive) {
      // Check if we have the current model's comparison data
      if (!this.currentModelComparisonData || !this.currentModelComparisonData.comparison) {
        console.warn('No comparison data available for Project Synapse');
        return 0;
      }

      // For Project Synapse, we need to extract the AI model's stylometric data from the comparison sections
      // The backend structure is: comparison.section_X.ai_generated.features.{analysisType}.{metricName}
      const sections = this.currentModelComparisonData.comparison;
      let totalValue = 0;
      let sectionCount = 0;

      // Normalize the metric name to match backend structure
      const normalizedMetricName = metricName.toLowerCase().replace(/ /g, '_');

      // Iterate through all sections to get the average AI model value
      for (const sectionKey in sections) {
        const section = sections[sectionKey];
        if (section && section.ai_generated && section.ai_generated.features && section.ai_generated.features[this.currentAnalysis]) {
          const aiFeatures = section.ai_generated.features[this.currentAnalysis];

          // Try different possible metric name formats
          let metricValue = null;

          // Direct match
          if (aiFeatures[normalizedMetricName] !== undefined) {
            metricValue = aiFeatures[normalizedMetricName];
          }
          // Try with original casing
          else if (aiFeatures[metricName] !== undefined) {
            metricValue = aiFeatures[metricName];
          }
          // Try camelCase version
          else if (aiFeatures[normalizedMetricName.replace(/_([a-z])/g, (g) => g[1].toUpperCase())] !== undefined) {
            metricValue = aiFeatures[normalizedMetricName.replace(/_([a-z])/g, (g) => g[1].toUpperCase())];
          }
          // Try exact name matching from the stylometricMetrics to find the corresponding backend key
          else {
            // Look for partial matches in case of naming differences
            for (const [key, value] of Object.entries(aiFeatures)) {
              if (key.includes(normalizedMetricName) || normalizedMetricName.includes(key)) {
                metricValue = value as number;
                break;
              }
            }
          }

          if (metricValue !== null && !isNaN(Number(metricValue))) {
            totalValue += Number(metricValue);
            sectionCount++;
          }
        }
      }

      // Return the average value across all sections, or 0 if no valid data found
      if (sectionCount > 0) {
        const avgValue = totalValue / sectionCount;
        return avgValue;
      }

      // Fallback: try to get from document-level stylometrics if available
      if (this.currentModelComparisonData.document_stylometrics?.features?.[this.currentAnalysis]?.[normalizedMetricName] !== undefined) {
        const docValue = this.currentModelComparisonData.document_stylometrics.features[this.currentAnalysis][normalizedMetricName];
        return Number(docValue);
      }

      return 0;
    } else {
      if (this.selectedSubmissionId === 'all' && this.previousSubmissions?.all) {
        // Get value from averaged metrics
        const metricData = this.previousSubmissions.all[this.currentAnalysis]?.find(
          (m: any) => m.name === metricName
        );
        return metricData?.value ?? 0;
      } else if (this.selectedSubmissionId === 'none' && this.previousSubmissions?.all) {
        // If no specific comparison is selected, use 'all' as default
        const metricData = this.previousSubmissions.all[this.currentAnalysis]?.find(
          (m: any) => m.name === metricName
        );
        return metricData?.value ?? 0;
      } else if (this.previousSubmissions?.individual) {
        // Get value from specific submission
        const selectedSubmission = this.previousSubmissions.individual.find(
          (sub: any) => sub.filename === this.selectedSubmissionId
        );
        const metricData = selectedSubmission?.metrics[this.currentAnalysis]?.find(
          (m: any) => m.name === metricName
        );
        return metricData?.value ?? 0;
      }
    }
    return 0;
  }

  // Helper method to format metric name as key
  private formatMetricKey(metricName: string): string {
    return metricName.toLowerCase().replace(/ /g, '_');
  }

  // Method to get CSS class for value box
  public getValueBoxClass(metricName: string, currentValue: number, previousValue: number): string {
    if (this.isProjectSynapseActive) {
      // For AI detection, FLAG if values are TOO SIMILAR (difference is LESS than threshold)
      return Math.abs(currentValue - previousValue) < this.metricsThresholds[this.formatMetricKey(metricName)]
        ? 'current exceeds-threshold'
        : 'current';
    } else {
      // For regular authorship comparison, FLAG if values are TOO DIFFERENT (difference is GREATER than threshold)
      return Math.abs(currentValue - previousValue) > this.metricsThresholds[this.formatMetricKey(metricName)]
        ? 'current exceeds-threshold'
        : 'current';
    }
  }

  getFlaggedMetrics(): number {
    if (!this.stylometricMetrics || !this.stylometricMetrics[this.currentAnalysis]) {
      console.warn(`No metrics data available for ${this.currentAnalysis} analysis`);
      return 0;
    }

    // Track metrics for detailed logging
    const flaggedMetricDetails = [];
    const unflaggedMetricDetails = [];

    const flaggedCount = this.stylometricMetrics[this.currentAnalysis].filter(metric => {
      const currentValue = Number(metric.value);
      const previousValue = this.getPreviousMetricValue(metric.name);
      const threshold = this.metricsThresholds[this.formatMetricKey(metric.name)];

      // Only include if we have valid values and the threshold is defined
      if (!isNaN(currentValue) && !isNaN(previousValue) && threshold !== undefined) {
        const diff = Math.abs(currentValue - previousValue);
        let isFlagged;

        if (this.isProjectSynapseActive) {
          // For AI detection, FLAG if values are TOO SIMILAR (difference is LESS than threshold)
          isFlagged = diff < threshold;
        } else {
          // For regular authorship comparison, FLAG if values are TOO DIFFERENT (difference is GREATER than threshold)
          isFlagged = diff > threshold;
        }

        // Add to appropriate tracking array
        const metricInfo = {
          name: metric.name,
          current: currentValue,
          previous: previousValue,
          diff,
          threshold,
          isFlagged
        };

        if (isFlagged) {
          flaggedMetricDetails.push(metricInfo);
        } else {
          unflaggedMetricDetails.push(metricInfo);
        }

        return isFlagged;
      }

      return false;
    }).length;

    return flaggedCount;
  }

  private updateTabFlagCounts(): void {
    if (!this.stylometricMetrics) return;

    let totalFlaggedCount = 0;

    this.tabs = this.tabs.map(tab => {
      const analysisType = tab.id.split('-')[0];
      const metrics = this.stylometricMetrics[analysisType] || [];

      let flaggedCountForAnalysis = 0;


      const flaggedCount = metrics.filter(metric => {
        const currentValue = Number(metric.value);
        const previousValue = this.getPreviousMetricValueForAnalysis(analysisType, metric.name);
        const threshold = this.metricsThresholds[this.formatMetricKey(metric.name)];

        // Only flag if we have valid values and the threshold is defined
        if (!isNaN(currentValue) && !isNaN(previousValue) && threshold !== undefined) {
          const diff = Math.abs(currentValue - previousValue);
          let isFlagged = false;

          if (this.isProjectSynapseActive) {
            // Project Synapse: flag if TOO SIMILAR (diff LESS THAN threshold)
            isFlagged = diff < threshold;
          } else {
            // Regular: flag if TOO DIFFERENT (diff GREATER THAN threshold)
            isFlagged = diff > threshold;
          }

          if (isFlagged) {
            flaggedCountForAnalysis++;
          }

          return isFlagged;
        } else {
          if (this.isProjectSynapseActive) {
          }
        }

        return false;
      }).length;

      totalFlaggedCount += flaggedCount;


      return { ...tab, count: flaggedCount };
    });

    // Update metrics stats
    this.metricsStats = {
      total: this.stylometricMetrics[this.currentAnalysis]?.length || 0,
      flagged: this.getFlaggedMetrics(),
      unflagged: (this.stylometricMetrics[this.currentAnalysis]?.length || 0) - this.getFlaggedMetrics()
    };
  }

  // Add this helper method to the class
  private formatMultiLineLabel(text: string): string[] {
    const maxLength = 15;

    // Split words that make up the label
    const words = text.split(' ');
    if (words.length <= 2 || text.length <= maxLength) {
      return [text]; // Return as single line if it's short
    }

    return words;
  }

  // Add this method before interpretWithAI
  isAnalysisInCache(): boolean {
    const cacheKey = this.generateCacheKey();
    return !!localStorage.getItem(cacheKey);
  }

  // Create a new method to generate consistent cache keys
  private generateCacheKey(): string {
    // Include project synapse state in the cache key
    const synapseState = this.isProjectSynapseActive ? 'synapse' : 'standard';

    // Add a hash of the current and previous metrics to ensure uniqueness
    let metricsHash = '';
    if (this.stylometricMetrics) {
      // Create a simple hash using the first metric value from each category
      const categories = Object.keys(this.stylometricMetrics);
      if (categories.length > 0) {
        metricsHash = categories
          .map(category => {
            const metrics = this.stylometricMetrics[category];
            return metrics && metrics.length > 0 ? metrics[0].value?.toString().substring(0, 5) : '';
          })
          .join('_');
      }
    }

    // Include previous submission info
    let previousMetricsInfo = '';
    if (this.selectedSubmissionId === 'all') {
      if (this.previousSubmissions?.all) {
        // Use the first metric from each category if available
        const categories = Object.keys(this.previousSubmissions.all);
        if (categories.length > 0) {
          previousMetricsInfo = categories
            .map(category => {
              const metrics = this.previousSubmissions.all[category];
              return metrics && metrics.length > 0 ? metrics[0].value?.toString().substring(0, 5) : '';
            })
            .join('_');
        }
      }
    } else if (this.previousSubmissions?.individual) {
      const selectedSubmission = this.previousSubmissions.individual.find(
        submission => submission.filename === this.selectedSubmissionId
      );
      if (selectedSubmission?.metrics) {
        // Create a simple identifier based on the first metric value from each category
        const categories = Object.keys(selectedSubmission.metrics);
        if (categories.length > 0) {
          previousMetricsInfo = categories
            .map(category => {
              const metrics = selectedSubmission.metrics[category];
              return metrics && metrics.length > 0 ? metrics[0].value?.toString().substring(0, 5) : '';
            })
            .join('_');
        }
      }
    }

    // Include information about flagged metrics
    const flaggedInfo = this.getFlaggedMetricsInfo();

    // Create a final cache key that includes all relevant data
    return `stylometric_analysis_${this.selectedTabId}_${this.selectedSubmissionId}_${synapseState}_${metricsHash}_${previousMetricsInfo}_${flaggedInfo}`;
  }

  // Helper method to get a string representation of flagged metrics
  private getFlaggedMetricsInfo(): string {
    let flaggedInfo = '';

    // Get counts of flagged metrics for each category
    if (this.stylometricMetrics) {
      const categories = Object.keys(this.stylometricMetrics);
      if (categories.length > 0) {
        flaggedInfo = categories
          .map(category => {
            const metrics = this.stylometricMetrics[category] || [];
            // Count flagged metrics for this category
            let flaggedCount = 0;

            for (const metric of metrics) {
              const currentValue = Number(metric.value);
              const previousValue = this.getPreviousMetricValue(metric.name);
              const threshold = this.metricsThresholds[this.formatMetricKey(metric.name)];

              if (!isNaN(currentValue) && !isNaN(previousValue) && threshold !== undefined) {
                const diff = Math.abs(currentValue - previousValue);
                const isFlagged = this.isProjectSynapseActive
                  ? diff < threshold  // Project Synapse: flag if TOO SIMILAR
                  : diff > threshold; // Regular: flag if TOO DIFFERENT

                if (isFlagged) {
                  flaggedCount++;
                }
              }
            }

            return `${category}:${flaggedCount}`;
          })
          .join('_');
      }
    }

    return flaggedInfo;
  }

  // Add this getter for the template
  get interpretButtonText(): string {
    return this.isAnalysisInCache() ? 'View AI Interpretation' : 'Interpret with AI';
  }

  // Update the existing interpretWithAI method
  async interpretWithAI(): Promise<any> {
    try {
      let analysis: any;
      let previousMetrics;

      // Generate cache key first
      const cacheKey = this.generateCacheKey();

      // Check cache before opening dialog
      const cachedData = localStorage.getItem(cacheKey);
      if (cachedData) {
        analysis = JSON.parse(cachedData);
        // Open dialog immediately with cached data
        this.dialog.open(AiAnalysisComponent, {
          height: '90vh',
          width: '60vw',
          ariaLabel: 'AI Analysis Results',
          ariaLabelledBy: 'ai-analysis-dialog',
          data: {
            llmAnalysis: analysis,
            isLoading: false,
            isNewAnalysis: false, // No streaming for cached data
            author: this.author,
            submissionId: this.submissionId,
            isOnIA: this.isOnIA,
            title: 'Stylometric Analysis - AI Interpretation',
            analysisType: 'stylometric'
          }
        });
        return analysis;
      }

      // For new analysis, open dialog in loading state first
      const dialogRef = this.dialog.open(AiAnalysisComponent, {
        height: '90vh',
        width: '60vw',
        ariaLabel: 'AI Analysis Results',
        ariaLabelledBy: 'ai-analysis-dialog',
        data: {
          llmAnalysis: null,
          isLoading: true,
          isNewAnalysis: false,
          author: this.author,
          submissionId: this.submissionId,
          isOnIA: this.isOnIA,
          title: 'Stylometric Analysis - AI Interpretation',
          analysisType: 'stylometric'
        }
      });

      // Get metrics for API request
      if (this.isProjectSynapseActive) {
        // For Project Synapse mode, construct AI model metrics from comparison data
        if (this.currentModelComparisonData) {
          previousMetrics = this.constructAIModelMetrics();
        } else {
          console.warn('No Project Synapse comparison data available');
          previousMetrics = null;
        }
      } else {
        // Regular mode: use previous submissions data
        if (this.previousSubmissions) {
          if (this.selectedSubmissionId === 'all') {
            previousMetrics = this.previousSubmissions.all;
          } else {
            const selectedSubmission = this.previousSubmissions.individual?.find(
              submission => submission.filename === this.selectedSubmissionId
            );
            previousMetrics = selectedSubmission?.metrics;
          }
        } else {
          // If no previous submissions data, set to null
          previousMetrics = null;
        }
      }

      // Prepare the flagged metrics information for better AI analysis
      const flaggedMetricsInfo = this.prepareFlaggedMetricsData();

      // Make the API request
      analysis = await new Promise((resolve, reject) => {
        this.stylometricState.getStylometricAnalysis(
          this.stylometricMetrics,
          previousMetrics,
          this.metricsThresholds,
          this.isProjectSynapseActive,  // Pass Project Synapse state
          flaggedMetricsInfo            // Pass flagged metrics data
        ).subscribe({
          next: (result) => {
            localStorage.setItem(cacheKey, JSON.stringify(result));
            resolve(result);
          },
          error: (error) => reject(error)
        });
      });

      if (analysis) {
        dialogRef.componentInstance.updateDialogData({
          llmAnalysis: analysis,
          isLoading: false,
          isNewAnalysis: true,
          author: this.author,
          submissionId: this.submissionId,
          isOnIA: this.isOnIA,
          title: 'Stylometric Analysis - AI Interpretation',
          analysisType: 'stylometric'
        });
      }

      return analysis;
    } catch (error) {
      console.error('Error getting stylometric analysis:', error);
      throw error;
    }
  }

  // Helper method to construct AI model metrics for Project Synapse mode
  private constructAIModelMetrics(): any {
    if (!this.currentModelComparisonData) {
      return null;
    }

    const aiMetrics: any = {};

    // Process each analysis type (lexical, syntactic, structural, readability)
    for (const analysisType of Object.keys(this.stylometricMetrics)) {
      const currentTypeMetrics = this.stylometricMetrics[analysisType] || [];
      aiMetrics[analysisType] = [];

      // For each metric in the current type, get the AI model's corresponding value
      for (const metric of currentTypeMetrics) {
        const aiValue = this.getPreviousMetricValueForAnalysis(analysisType, metric.name);
        aiMetrics[analysisType].push({
          name: metric.name,
          value: aiValue
        });
      }
    }

    return aiMetrics;
  }

  // Helper method to prepare flagged metrics data for AI analysis
  private prepareFlaggedMetricsData(): any {
    const result: any = {};

    // Process each analysis type
    for (const analysisType of Object.keys(this.stylometricMetrics)) {
      const metrics = this.stylometricMetrics[analysisType] || [];

      result[analysisType] = metrics.map(metric => {
        const currentValue = Number(metric.value);
        const previousValue = this.getPreviousMetricValue(metric.name);
        const threshold = this.metricsThresholds[this.formatMetricKey(metric.name)];
        const diff = Math.abs(currentValue - previousValue);

        // Determine if the metric is flagged based on the current mode
        const isFlagged = this.isProjectSynapseActive
          ? diff < threshold  // Project Synapse: flag if TOO SIMILAR (less than threshold)
          : diff > threshold; // Regular: flag if TOO DIFFERENT (greater than threshold)

        return {
          name: metric.name,
          current: currentValue,
          previous: previousValue,
          difference: diff,
          threshold: threshold,
          isFlagged: isFlagged
        };
      });
    }

    return result;
  }

  private getPreviousMetricValueForAnalysis(analysisType: string, metricName: string): number {
    if (this.isProjectSynapseActive) {
      // Check if we have the current model's comparison data
      if (!this.currentModelComparisonData || !this.currentModelComparisonData.comparison) {
        console.warn('No comparison data available for Project Synapse');
        return 0;
      }

      // For Project Synapse, we need to extract the AI model's stylometric data from the comparison sections
      // The backend structure is: comparison.section_X.ai_generated.features.{analysisType}.{metricName}
      const sections = this.currentModelComparisonData.comparison;
      let totalValue = 0;
      let sectionCount = 0;

      // Normalize the metric name to match backend structure
      const normalizedMetricName = metricName.toLowerCase().replace(/ /g, '_');

      // Iterate through all sections to get the average AI model value
      for (const sectionKey in sections) {
        const section = sections[sectionKey];
        if (section && section.ai_generated && section.ai_generated.features && section.ai_generated.features[analysisType]) {
          const aiFeatures = section.ai_generated.features[analysisType];

          // Try different possible metric name formats
          let metricValue = null;

          // Direct match
          if (aiFeatures[normalizedMetricName] !== undefined) {
            metricValue = aiFeatures[normalizedMetricName];
          }
          // Try with original casing
          else if (aiFeatures[metricName] !== undefined) {
            metricValue = aiFeatures[metricName];
          }
          // Try camelCase version
          else if (aiFeatures[normalizedMetricName.replace(/_([a-z])/g, (g) => g[1].toUpperCase())] !== undefined) {
            metricValue = aiFeatures[normalizedMetricName.replace(/_([a-z])/g, (g) => g[1].toUpperCase())];
          }
          // Try exact name matching from the stylometricMetrics to find the corresponding backend key
          else {
            // Look for partial matches in case of naming differences
            for (const [key, value] of Object.entries(aiFeatures)) {
              if (key.includes(normalizedMetricName) || normalizedMetricName.includes(key)) {
                metricValue = value as number;
                break;
              }
            }
          }

          if (metricValue !== null && !isNaN(Number(metricValue))) {
            totalValue += Number(metricValue);
            sectionCount++;
          }
        }
      }

      // Return the average value across all sections, or 0 if no valid data found
      if (sectionCount > 0) {
        const avgValue = totalValue / sectionCount;
        return avgValue;
      }

      // Fallback: try to get from document-level stylometrics if available
      if (this.currentModelComparisonData.document_stylometrics?.features?.[analysisType]?.[normalizedMetricName] !== undefined) {
        const docValue = this.currentModelComparisonData.document_stylometrics.features[analysisType][normalizedMetricName];
        return Number(docValue);
      }

      console.warn(`Project Synapse Analysis - Could not find metric ${analysisType}.${metricName}`);
      return 0;
    } else {
      if (this.selectedSubmissionId === 'all' && this.previousSubmissions?.all) {
        // Get value from averaged metrics
        const metricData = this.previousSubmissions.all[analysisType]?.find(
          (m: any) => m.name === metricName
        );
        return metricData?.value ?? 0;
      } else if (this.selectedSubmissionId === 'none' && this.previousSubmissions?.all) {
        // If no specific comparison is selected, use 'all' as default
        const metricData = this.previousSubmissions.all[analysisType]?.find(
          (m: any) => m.name === metricName
        );
        return metricData?.value ?? 0;
      } else if (this.previousSubmissions?.individual) {
        // Get value from specific submission
        const selectedSubmission = this.previousSubmissions.individual.find(
          (sub: any) => sub.filename === this.selectedSubmissionId
        );
        const metricData = selectedSubmission?.metrics[analysisType]?.find(
          (m: any) => m.name === metricName
        );
        return metricData?.value ?? 0;
      }
    }
    return 0;
  }
}
